import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../../lib/api";
import { formatNum, ROLE_LABELS } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input, Label, Textarea } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { toast } from "sonner";
import { Settings2, Users, Ban, RotateCcw, KeyRound, ShieldOff, Copy, Trash2, AlertTriangle, Download, Plus, CalendarPlus, History } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import PlatformTenantDialog from "../../components/platform/PlatformTenantDialog";
import { PageShell } from "./PageShell";

function CreateTenantDialog({ onClose, onDone }) {
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ company_name: "", uen: "", admin_name: "", admin_email: "", admin_password: "", package_code: "growth", mode: "trial", seats: 5, billing_cycle: "monthly" });
  useEffect(() => { api.get("/onboarding/packages").then((r) => setPackages(r.data.packages || [])).catch(() => {}); }, []);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/tenants", { ...f, uen: f.uen || null, seats: Number(f.seats) || 1 });
      toast.success(`${data.name} created — ${data.mode === "trial" ? "15-day trial" : "paid plan active"} on ${data.package_code}. Admin: ${data.admin_email}`);
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const pkg = packages.find((p) => p.code === f.package_code);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="create-tenant-dialog">
        <DialogHeader><DialogTitle className="font-display">Create a new tenant</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Company name *</Label>
            <Input value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} placeholder="e.g. Meridian Foods Pte Ltd" data-testid="ct-company-input" /></div>
          <div><Label>UEN (optional)</Label>
            <Input value={f.uen} onChange={(e) => setF({ ...f, uen: e.target.value.toUpperCase() })} placeholder="201812345K" data-testid="ct-uen-input" /></div>
          <div><Label>Admin name *</Label>
            <Input value={f.admin_name} onChange={(e) => setF({ ...f, admin_name: e.target.value })} data-testid="ct-admin-name-input" /></div>
          <div><Label>Admin email *</Label>
            <Input type="email" value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} data-testid="ct-admin-email-input" /></div>
          <div><Label>Temp password * (min 8)</Label>
            <Input value={f.admin_password} onChange={(e) => setF({ ...f, admin_password: e.target.value })} autoComplete="off" data-testid="ct-admin-password-input" /></div>
          <div><Label>Plan *</Label>
            <Select value={f.package_code} onValueChange={(v) => setF({ ...f, package_code: v })}>
              <SelectTrigger data-testid="ct-package-select"><SelectValue /></SelectTrigger>
              <SelectContent>{packages.map((p) => <SelectItem key={p.code} value={p.code}>{p.name} — S${p.price_sgd}/user/mo</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Start as</Label>
            <div className="flex border border-border rounded-sm overflow-hidden" data-testid="ct-mode-toggle">
              {["trial", "paid"].map((m) => (
                <button key={m} type="button" onClick={() => setF({ ...f, mode: m })}
                  className={`flex-1 text-xs py-1.5 transition-colors ${f.mode === m ? "bg-accent text-accent-foreground font-semibold" : "text-muted-foreground hover:bg-surface-hover"}`}
                  data-testid={`ct-mode-${m}`}>
                  {m === "trial" ? "15-day trial" : "Paid"}
                </button>
              ))}
            </div></div>
          {f.mode === "paid" && (
            <>
              <div><Label>Seats</Label>
                <Input type="number" min={1} max={500} value={f.seats} onChange={(e) => setF({ ...f, seats: e.target.value })} data-testid="ct-seats-input" /></div>
              <div><Label>Billing cycle</Label>
                <Select value={f.billing_cycle} onValueChange={(v) => setF({ ...f, billing_cycle: v })}>
                  <SelectTrigger data-testid="ct-cycle-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="annual">Annual (2 months free)</SelectItem></SelectContent>
                </Select></div>
            </>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {f.mode === "trial"
            ? `Starts a 15-day trial with Growth-level features; ${pkg?.name || "the chosen plan"} applies after conversion.`
            : `Activates a paid ${pkg?.name || ""} subscription immediately (simulated billing, no Stripe charge).`}
          {" "}The admin signs in with the email + temp password and completes the setup checklist.
        </p>
        <Button variant="primary" className="w-full" onClick={go} data-testid="ct-create-btn"
          disabled={busy || f.company_name.trim().length < 2 || f.admin_name.trim().length < 2 || !f.admin_email.includes("@") || f.admin_password.length < 8}>
          {busy ? "Creating…" : "Create tenant"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }) : "—");

function StatusBadge({ t }) {
  if (t.suspended) return <Badge variant="destructive" className="text-[10px] uppercase">Suspended</Badge>;
  if (!t.onboarded) return <Badge variant="warning" className="text-[10px] uppercase">Pending</Badge>;
  const s = t.subscription?.status;
  if (s === "expired") return <Badge variant="destructive" className="text-[10px] uppercase">Trial expired</Badge>;
  if (s === "trialing") return <Badge variant="warning" className="text-[10px] uppercase">Trialing</Badge>;
  return <Badge variant="success" className="text-[10px] uppercase">Active</Badge>;
}

function UsersDialog({ tenant, onClose }) {
  const [users, setUsers] = useState(null);
  const [temp, setTemp] = useState(null);
  const load = useCallback(() => {
    api.get(`/platform/tenants/${tenant.id}/users`).then((r) => setUsers(r.data.users)).catch((e) => toast.error(apiErr(e)));
  }, [tenant.id]);
  useEffect(() => { load(); }, [load]);

  const resetMfa = async (u) => {
    if (!window.confirm(`Clear two-factor authentication for ${u.email}? They will sign in with password only and can re-enroll.`)) return;
    try {
      const { data } = await api.post(`/platform/users/${u.id}/reset-mfa`);
      toast.success(data.message); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const resetPassword = async (u) => {
    if (!window.confirm(`Issue a temporary password for ${u.email}? Their current password stops working immediately.`)) return;
    try {
      const { data } = await api.post(`/platform/users/${u.id}/reset-password`);
      setTemp({ email: u.email, password: data.temp_password });
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rescue-users-dialog">
        <DialogHeader><DialogTitle className="font-display">Account rescue — {tenant.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">User account metadata only. Every rescue action is written to the operator audit log.</p>
        {temp && (
          <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 text-sm" data-testid="temp-password-box">
            <div className="text-xs font-semibold text-amber-500 mb-1">Temporary password for {temp.email} — shown only once</div>
            <div className="flex items-center gap-2">
              <code className="font-mono text-sm bg-background border border-border rounded-sm px-2 py-1" data-testid="temp-password-value">{temp.password}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(temp.password); toast.success("Copied"); }} data-testid="copy-temp-password"><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        )}
        {!users ? <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div> : (
          <div className="space-y-1.5">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 border border-border rounded-sm p-2.5" data-testid={`rescue-user-${u.email}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{u.name} <span className="text-muted-foreground font-normal">· {ROLE_LABELS[u.role] || u.role}</span></div>
                  <div className="text-[11px] font-mono text-muted-foreground truncate">{u.email} · last sign-in {fmtDate(u.last_login_at)}</div>
                </div>
                <Badge variant={u.mfa_enabled ? "success" : "outline"} className="text-[9px] shrink-0">{u.mfa_enabled ? "2FA on" : "2FA off"}</Badge>
                <Button size="sm" variant="ghost" title="Reset MFA" disabled={!u.mfa_enabled} onClick={() => resetMfa(u)} data-testid={`reset-mfa-${u.email}`}><ShieldOff className="h-3.5 w-3.5 text-amber-400" /></Button>
                <Button size="sm" variant="ghost" title="Reset password" onClick={() => resetPassword(u)} data-testid={`reset-password-${u.email}`}><KeyRound className="h-3.5 w-3.5 text-red-400" /></Button>
              </div>
            ))}
            {!users.length && <div className="text-sm text-muted-foreground py-4 text-center">No users in this tenant.</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SuspendDialog({ tenant, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/platform/tenants/${tenant.id}/suspend`, { reason: reason.trim() || null });
      toast.success(`${tenant.name} suspended — workspace is now read-only`);
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="suspend-dialog">
        <DialogHeader><DialogTitle className="font-display">Suspend {tenant.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Users can still sign in and read their data, but all changes are blocked until reactivation. Their admins receive an in-app notice. No tenant data is accessed or altered.</p>
        <div><Label>Reason (shared with the tenant)</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Overdue invoices — contact billing" data-testid="suspend-reason" /></div>
        <Button variant="destructive" className="w-full" onClick={go} disabled={busy} data-testid="suspend-confirm">
          {busy ? "Suspending…" : "Suspend tenant"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ReactivateDialog({ tenant, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/platform/tenants/${tenant.id}/reactivate`);
      toast.success(`${tenant.name} reactivated`);
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reactivate-dialog">
        <DialogHeader><DialogTitle className="font-display">Reactivate {tenant.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Full write access is restored immediately and the tenant's admins receive an in-app notice.
          {tenant.suspended_reason && <> Suspension reason on record: "{tenant.suspended_reason}".</>}
        </p>
        <Button variant="primary" className="w-full" onClick={go} disabled={busy} data-testid="reactivate-confirm">
          {busy ? "Reactivating…" : "Reactivate tenant"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ExtendTrialDialog({ tenant, onClose, onDone }) {
  const [amount, setAmount] = useState(14);
  const [unit, setUnit] = useState("days");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const sub = tenant.subscription || {};
  const curEnd = sub.trial_end ? new Date(sub.trial_end) : null;
  const paid = sub.status === "active";
  useEffect(() => {
    api.get(`/platform/tenants/${tenant.id}/trial-history`).then((r) => setHistory(r.data.extensions || [])).catch(() => {});
  }, [tenant.id]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/tenants/${tenant.id}/extend-trial`, { amount: Number(amount), unit });
      toast.success(`Trial extended — now ends ${new Date(data.trial_end).toLocaleDateString()} (${data.days_remaining} day(s) left)`);
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="extend-trial-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><CalendarPlus className="h-4 w-4 text-accent" /> Extend trial — {tenant.name}</DialogTitle></DialogHeader>
        {paid ? (
          <p className="text-sm text-muted-foreground" data-testid="extend-trial-paid-note">
            This tenant is on an active paid plan, so there's no trial to extend.
          </p>
        ) : (
          <>
            <div className="text-xs text-muted-foreground" data-testid="extend-trial-current">
              Current status: <span className="text-foreground font-medium">{sub.status || "none"}</span>
              {curEnd && <> · trial ends <span className="text-foreground font-medium">{curEnd.toLocaleDateString()}</span></>}
              {(sub.status === "expired") && <span className="text-amber-400"> — expired, workspace read-only</span>}
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1">
              Extends from the current trial end, or from today if it has already expired, and re-opens a read-only workspace.
            </p>
            <div className="grid grid-cols-2 gap-2 items-end">
              <div><Label>Extend by</Label>
                <Input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="extend-trial-amount" /></div>
              <div><Label>Unit</Label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger data-testid="extend-trial-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="months">Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button variant="primary" className="w-full" onClick={go} disabled={busy || !amount || Number(amount) <= 0} data-testid="extend-trial-confirm">
              {busy ? "Extending…" : `Extend trial by ${amount || 0} ${unit}`}
            </Button>
          </>
        )}
        <div className="border-t border-border pt-3 mt-1" data-testid="trial-history">
          <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Extension history</div>
          {history.length === 0
            ? <div className="text-[11px] text-muted-foreground" data-testid="trial-history-empty">No trial extensions yet.</div>
            : <div className="space-y-1.5 max-h-44 overflow-y-auto">
              {history.map((h, i) => (
                <div key={i} className="text-[11px] flex items-start justify-between gap-2 border-b border-border/40 pb-1" data-testid={`trial-history-${i}`}>
                  <div className="min-w-0">
                    <div className="text-foreground">+{h.amount} {h.unit} → ends {h.to ? new Date(h.to).toLocaleDateString() : "—"}</div>
                    <div className="text-muted-foreground truncate">by {h.by}</div>
                  </div>
                  <div className="text-muted-foreground shrink-0">{h.at ? new Date(h.at).toLocaleDateString() : ""}</div>
                </div>
              ))}
            </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function BulkExtendTrialDialog({ tenants, onClose, onDone }) {
  const [amount, setAmount] = useState(14);
  const [unit, setUnit] = useState("days");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const eligible = tenants.filter((t) => ["trialing", "expired"].includes(t.subscription?.status));
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/tenants/bulk-extend-trial`, {
        tenant_ids: tenants.map((t) => t.id), amount: Number(amount), unit });
      setResult(data);
      toast.success(`Extended ${data.extended_count} trial(s)${data.skipped_count ? `, skipped ${data.skipped_count}` : ""}`);
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-extend-trial-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><CalendarPlus className="h-4 w-4 text-accent" /> Extend trial — {tenants.length} selected</DialogTitle></DialogHeader>
        {result ? (
          <div className="text-sm space-y-2" data-testid="bulk-extend-result">
            <div className="text-[#00E65B]">Extended {result.extended_count} trial(s).</div>
            {result.skipped_count > 0 && (
              <div className="text-muted-foreground">
                <div className="text-amber-400 mb-1">Skipped {result.skipped_count}:</div>
                {result.skipped.map((s, i) => <div key={i} className="text-[11px]">· {s.name || s.tenant_id} — {s.reason}</div>)}
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={onClose} data-testid="bulk-extend-close">Close</Button>
          </div>
        ) : (
          <>
            <p className="text-[11px] text-muted-foreground">
              Applies to trialing/expired tenants in your selection ({eligible.length} of {tenants.length}). Active paid plans are skipped automatically.
            </p>
            <div className="grid grid-cols-2 gap-2 items-end">
              <div><Label>Extend by</Label>
                <Input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="bulk-extend-amount" /></div>
              <div><Label>Unit</Label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger data-testid="bulk-extend-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="months">Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button variant="primary" className="w-full" onClick={go} disabled={busy || !amount || Number(amount) <= 0} data-testid="bulk-extend-confirm">
              {busy ? "Extending…" : `Extend ${eligible.length} trial(s) by ${amount || 0} ${unit}`}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PurgeDialog({ tenant, onClose, onDone }) {
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const match = confirmName.trim() === tenant.name;
  const exportArchive = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/platform/tenants/${tenant.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `${tenant.slug}-archive.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Archive downloaded — safe to purge");
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(false); }
  };
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/tenants/${tenant.id}/purge`, { confirm_name: confirmName.trim() });
      toast.success(`${data.tenant} purged — ${formatNum(data.total_documents)} document(s) permanently deleted`);
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="purge-dialog">
        <DialogHeader><DialogTitle className="font-display text-red-400 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Purge {tenant.name}</DialogTitle></DialogHeader>
        <div className="border border-red-500/40 bg-red-500/10 rounded-sm p-3 text-xs space-y-1.5" data-testid="purge-warning">
          <div className="font-semibold text-red-400 uppercase tracking-wide">This cannot be undone</div>
          <p className="text-muted-foreground">
            Every record belonging to this instance will be permanently deleted:
            {" "}{tenant.users_count} user account(s), {formatNum(tenant.volume.data_points)} data point(s),
            {" "}{formatNum(tenant.volume.documents)} document(s), {formatNum(tenant.volume.reports)} report(s),
            plus all financing, grants, subscriptions and notifications. The purge is recorded in the operator audit log.
          </p>
        </div>
        <Button variant="outline" className="w-full" onClick={exportArchive} disabled={exporting} data-testid="purge-export-btn">
          <Download className="h-3.5 w-3.5 mr-2" />{exporting ? "Preparing archive…" : "Download data archive (.zip) first"}
        </Button>
        <div>
          <Label>Type the instance name to confirm: <span className="font-mono text-foreground">{tenant.name}</span></Label>
          <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
            placeholder={tenant.name} autoComplete="off" data-testid="purge-confirm-name" />
          {confirmName && !match && <div className="text-[11px] text-red-400 mt-1" data-testid="purge-name-mismatch">Name does not match.</div>}
        </div>
        <Button variant="destructive" className="w-full" onClick={go} disabled={!match || busy} data-testid="purge-confirm-btn">
          {busy ? "Purging…" : "Permanently purge this instance"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function BulkSuspendDialog({ tenants, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const targets = tenants.filter((t) => !t.suspended);
  const go = async () => {
    setBusy(true);
    let ok = 0;
    for (const t of targets) {
      try { await api.post(`/platform/tenants/${t.id}/suspend`, { reason: reason.trim() || null }); ok += 1; }
      catch (e) { toast.error(`${t.name}: ${apiErr(e)}`); }
    }
    toast.success(`${ok} tenant(s) suspended`);
    onDone(); onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-suspend-dialog">
        <DialogHeader><DialogTitle className="font-display">Suspend {targets.length} tenant(s)</DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto border border-border rounded-sm p-2">
          {targets.map((t) => <div key={t.id}>{t.name}</div>)}
          {tenants.length > targets.length && <div className="text-amber-500 mt-1">{tenants.length - targets.length} already suspended — skipped.</div>}
        </div>
        <div><Label>Reason (shared with the tenants)</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Scheduled decommissioning" data-testid="bulk-suspend-reason" /></div>
        <Button variant="destructive" className="w-full" onClick={go} disabled={busy || !targets.length} data-testid="bulk-suspend-confirm">
          {busy ? "Suspending…" : `Suspend ${targets.length} tenant(s)`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function BulkPurgeDialog({ tenants, onClose, onDone }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState({});
  const activeCount = tenants.filter((t) => !t.suspended).length;
  const armed = confirmText.trim() === "PURGE";
  const go = async () => {
    setBusy(true);
    let docs = 0, ok = 0;
    for (const t of tenants) {
      setProgress((p) => ({ ...p, [t.id]: "purging" }));
      try {
        if (!t.suspended) await api.post(`/platform/tenants/${t.id}/suspend`, { reason: "Bulk purge" });
        const { data } = await api.post(`/platform/tenants/${t.id}/purge`, { confirm_name: t.name });
        docs += data.total_documents; ok += 1;
        setProgress((p) => ({ ...p, [t.id]: "done" }));
      } catch (e) { setProgress((p) => ({ ...p, [t.id]: apiErr(e) })); }
    }
    toast.success(`${ok}/${tenants.length} instance(s) purged — ${formatNum(docs)} document(s) permanently deleted`);
    setBusy(false); setDone(true); onDone();
    if (ok === tenants.length) onClose();
  };
  const statusLabel = (t) => {
    const s = progress[t.id];
    if (!s) return null;
    if (s === "purging") return <span className="text-amber-400">purging…</span>;
    if (s === "done") return <span className="text-[#00E65B]">purged</span>;
    return <span className="text-red-400">{s}</span>;
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-purge-dialog">
        <DialogHeader><DialogTitle className="font-display text-red-400 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Purge {tenants.length} instance(s)</DialogTitle></DialogHeader>
        <div className="border border-red-500/40 bg-red-500/10 rounded-sm p-3 text-xs space-y-1.5" data-testid="bulk-purge-warning">
          <div className="font-semibold text-red-400 uppercase tracking-wide">This cannot be undone</div>
          <p className="text-muted-foreground">
            All data belonging to the instances below will be permanently deleted.
            {activeCount > 0 && <> <span className="text-amber-400">{activeCount} active instance(s) will be suspended first, then purged.</span></>}
            {" "}Each purge is recorded in the operator audit log.
          </p>
        </div>
        <div className="text-xs max-h-40 overflow-y-auto border border-border rounded-sm divide-y divide-border" data-testid="bulk-purge-list">
          {tenants.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-2 py-1.5">
              <span>{t.name}<span className="text-muted-foreground"> · {t.users_count} user(s) · {formatNum(t.volume.data_points)} data point(s)</span></span>
              {statusLabel(t)}
            </div>
          ))}
        </div>
        {!done ? (
          <>
            <div>
              <Label>Type <span className="font-mono text-foreground">PURGE</span> to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="PURGE" autoComplete="off" data-testid="bulk-purge-confirm-input" />
            </div>
            <Button variant="destructive" className="w-full" onClick={go} disabled={!armed || busy} data-testid="bulk-purge-confirm-btn">
              {busy ? "Purging…" : `Permanently purge ${tenants.length} instance(s)`}
            </Button>
          </>
        ) : (
          <Button variant="outline" className="w-full" onClick={onClose} data-testid="bulk-purge-close-btn">Close</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PlatformTenants() {
  const [rows, setRows] = useState(null);
  const [entTenant, setEntTenant] = useState(null);
  const [usersTenant, setUsersTenant] = useState(null);
  const [suspTenant, setSuspTenant] = useState(null);
  const [reactTenant, setReactTenant] = useState(null);
  const [extendTenant, setExtendTenant] = useState(null);
  const [purgeTenant, setPurgeTenant] = useState(null);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkSusp, setBulkSusp] = useState(false);
  const [bulkExtend, setBulkExtend] = useState(false);
  const [bulkPurge, setBulkPurge] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/platform/tenants").then((r) => setRows(r.data.tenants)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = (rows || []).filter((t) => (t.name || "").toLowerCase().includes(filter.trim().toLowerCase()));
  const selTenants = (rows || []).filter((t) => selected.has(t.id));
  const allFilteredSelected = filtered.length > 0 && filtered.every((t) => selected.has(t.id));
  const toggleOne = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((p) => {
    if (allFilteredSelected) { const n = new Set(p); filtered.forEach((t) => n.delete(t.id)); return n; }
    const n = new Set(p); filtered.forEach((t) => n.add(t.id)); return n;
  });
  const afterBulk = () => { setSelected(new Set()); load(); };

  return (
    <PageShell title="Tenant Console" testid="platform-tenants-page"
      subtitle="Manage and maintain every tenant — plan entitlements, lifecycle and account rescue. Metadata only: tenant business data stays private."
      actions={<Button variant="primary" size="sm" onClick={() => setCreateOpen(true)} data-testid="new-tenant-btn"><Plus className="h-3.5 w-3.5" /> New tenant</Button>}>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Input placeholder="Filter tenants by name…" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs h-8 text-sm" data-testid="tenant-filter" />
        {filter && <span className="text-[11px] text-muted-foreground">{filtered.length} match(es)</span>}
        {selTenants.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-muted-foreground" data-testid="selected-count">{selTenants.length} selected</span>
            <Button size="sm" variant="outline" onClick={() => setBulkSusp(true)} data-testid="bulk-suspend-btn">
              <Ban className="h-3.5 w-3.5 mr-1.5 text-red-400" /> Suspend selected
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkExtend(true)} data-testid="bulk-extend-trial-btn">
              <CalendarPlus className="h-3.5 w-3.5 mr-1.5 text-accent" /> Extend trial
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setBulkPurge(true)} data-testid="bulk-purge-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Purge selected
            </Button>
          </div>
        )}
      </div>
      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-8">
              <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll}
                className="h-3.5 w-3.5 accent-red-500 cursor-pointer align-middle" data-testid="tenant-select-all" />
            </TableHead>
            <TableHead>Tenant</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead>
            <TableHead className="text-right">Users</TableHead><TableHead className="text-right">2FA</TableHead>
            <TableHead className="text-right">Data / Docs / Reports</TableHead>
            <TableHead className="text-right">AI 30d</TableHead><TableHead>Last activity</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((t) => (
              <TableRow key={t.id} data-testid={`console-tenant-${t.slug}`}>
                <TableCell>
                  <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleOne(t.id)}
                    className="h-3.5 w-3.5 accent-red-500 cursor-pointer align-middle" data-testid={`tenant-select-${t.slug}`} />
                </TableCell>
                <TableCell>
                  <div className="font-medium text-sm">{t.name}</div>
                  <div className="text-[11px] text-muted-foreground">{t.industry_name || "—"} · since {fmtDate(t.created_at)}</div>
                </TableCell>
                <TableCell>{t.subscription?.package_code ? <Badge variant="outline" className="text-[10px]">{t.subscription.package_code}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                <TableCell><StatusBadge t={t} />{t.mfa_required && <Badge variant="outline" className="text-[9px] ml-1">2FA req.</Badge>}</TableCell>
                <TableCell className="text-right font-mono text-sm">{t.users_count}</TableCell>
                <TableCell className="text-right font-mono text-sm">{t.mfa_enrolled}/{t.users_count}</TableCell>
                <TableCell className="text-right font-mono text-xs">{formatNum(t.volume.data_points)} / {formatNum(t.volume.documents)} / {formatNum(t.volume.reports)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{formatNum(t.ai_calls_30d)}</TableCell>
                <TableCell className="text-xs">{fmtDate(t.last_activity)}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" title="Plan & entitlements" onClick={() => setEntTenant(t)} data-testid={`console-entitlements-${t.slug}`}><Settings2 className="h-3.5 w-3.5" /></Button>
                    {(t.subscription?.status === "trialing" || t.subscription?.status === "expired") && (
                      <Button size="sm" variant="ghost" title="Extend trial" onClick={() => setExtendTenant(t)} data-testid={`console-extend-trial-${t.slug}`}><CalendarPlus className="h-3.5 w-3.5 text-accent" /></Button>
                    )}
                    <Button size="sm" variant="ghost" title="Account rescue" onClick={() => setUsersTenant(t)} data-testid={`console-users-${t.slug}`}><Users className="h-3.5 w-3.5" /></Button>
                    {t.suspended
                      ? <Button size="sm" variant="ghost" title="Reactivate" onClick={() => setReactTenant(t)} data-testid={`console-reactivate-${t.slug}`}><RotateCcw className="h-3.5 w-3.5 text-[#00E65B]" /></Button>
                      : <Button size="sm" variant="ghost" title="Suspend" onClick={() => setSuspTenant(t)} data-testid={`console-suspend-${t.slug}`}><Ban className="h-3.5 w-3.5 text-red-400" /></Button>}
                    <Button size="sm" variant="ghost" title={t.suspended ? "Purge instance" : "Purge instance (suspend first)"}
                      disabled={!t.suspended} onClick={() => setPurgeTenant(t)} data-testid={`console-purge-${t.slug}`}>
                      <Trash2 className={`h-3.5 w-3.5 ${t.suspended ? "text-red-500" : "text-muted-foreground/40"}`} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows && !rows.length && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8 text-sm">No tenants.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {entTenant && <PlatformTenantDialog tenant={entTenant} onClose={() => setEntTenant(null)} onChanged={load} />}
      {extendTenant && <ExtendTrialDialog tenant={extendTenant} onClose={() => setExtendTenant(null)} onDone={load} />}
      {usersTenant && <UsersDialog tenant={usersTenant} onClose={() => setUsersTenant(null)} />}
      {suspTenant && <SuspendDialog tenant={suspTenant} onClose={() => setSuspTenant(null)} onDone={load} />}
      {reactTenant && <ReactivateDialog tenant={reactTenant} onClose={() => setReactTenant(null)} onDone={load} />}
      {purgeTenant && <PurgeDialog tenant={purgeTenant} onClose={() => setPurgeTenant(null)} onDone={load} />}
      {bulkSusp && <BulkSuspendDialog tenants={selTenants} onClose={() => setBulkSusp(false)} onDone={afterBulk} />}
      {bulkExtend && <BulkExtendTrialDialog tenants={selTenants} onClose={() => setBulkExtend(false)} onDone={afterBulk} />}
      {bulkPurge && <BulkPurgeDialog tenants={selTenants} onClose={() => setBulkPurge(false)} onDone={afterBulk} />}
      {createOpen && <CreateTenantDialog onClose={() => setCreateOpen(false)} onDone={load} />}
    </PageShell>
  );
}
